import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/** Sub-pixel slack, so a position a fraction of a pixel short still counts as reached. */
const REACH_TOLERANCE = 1;

/**
 * The highest leading-slide index worth selecting: the FIRST position at which the LAST slide is
 * fully in view. Past it the track is already at its end and stepping scrolls nothing.
 *
 * WHY NOT `slides.length - visibleSlides.length`, which is what the arrows used to divide by:
 * `visibleSlides` is filled by an IntersectionObserver at a 0.7 ratio
 * (`SLIDE_VISIBLITY_THRESHOLD` in assets/slideshow.js), so a card merely 70% on screen counts as
 * visible. At any viewport where the peeking next card crosses that line the count is one too
 * high and the subtraction stops the arrows a whole card early.
 *
 * Measured at 1920px before this existed: five 321px cards fit (5 x 337 = 1685) and the sixth
 * showed the remaining 235px = 73%, so `visibleSlides.length` was 6, the ceiling came out
 * 12 - 6 = 6, and the next arrow died with 694px of track unscrolled and the last card 390px off
 * screen. At 1440px the same maths happened to be right, which is why it read as viewport-specific.
 *
 * Scroll geometry has no threshold in it, so this reads that instead: where each slide lands when
 * selected, and where the track must be for the last card's trailing edge to clear the viewport.
 *
 * Assumes a horizontal left-to-right track — which this section's CSS builds, and the store ships
 * no RTL locale to contradict.
 *
 * @param {HTMLElement & Record<string, any>} carousel - A `<slideshow-component>` element.
 * @returns {number | null} An index within `[0, slides.length - 1]`, or `null` when the track
 *   cannot be measured — not laid out yet, or hidden.
 */
function lastReachableIndex(carousel) {
  const scroller = carousel.refs?.scroller;
  const slides = carousel.slides;
  if (!(scroller instanceof HTMLElement) || !slides?.length) return null;

  const viewport = scroller.clientWidth;

  // Nothing to measure: either the track has not been laid out yet, or it is display:none — the
  // mobile-only carousel above 750px is permanently in that state. Every measurement below would
  // come out 0 and read as "the whole track already fits", so say "unknown" and let the callers
  // leave the controls alone. Returning 0 here is what made the arrows come up dead on load.
  if (viewport === 0) return null;

  const lastIndex = slides.length - 1;
  const maxScroll = scroller.scrollWidth - viewport;

  // Genuinely no overflow — index 0 already shows every card.
  if (maxScroll <= 0) return 0;

  // Content-space offsets. `getBoundingClientRect()` is viewport-relative, so a slide's rect
  // shifts left as the track scrolls; subtracting the scroll from the origin adds it back and
  // makes these numbers independent of where the track happens to sit when this runs.
  //
  // The sign matters: `+ scrollLeft` here double-counts the scroll instead of cancelling it, and
  // every offset then drifts by twice the scroll. That reads as a ceiling that collapses as the
  // user advances — the arrows died three cards in rather than at the end.
  const origin = scroller.getBoundingClientRect().left - scroller.scrollLeft;
  const offsetOf = (/** @type {HTMLElement} */ slide) => slide.getBoundingClientRect().left - origin;

  // Slide 0 rests behind the start gutter, so its offset IS the gutter width — and selecting any
  // slide scrolls it to that same spot. Subtracting it turns an offset into a scroll position.
  const gutter = offsetOf(slides[0]);

  const lastSlide = slides[lastIndex];
  const scrollToClearLast = offsetOf(lastSlide) + lastSlide.getBoundingClientRect().width - viewport;

  for (let index = 0; index <= lastIndex; index++) {
    // `min`, because the browser clamps a scroll past the end: the final few slides all resolve
    // to the same real position, and the first of them is the one to stop at.
    const position = Math.min(offsetOf(slides[index]) - gutter, maxScroll);
    if (position >= scrollToClearLast - REACH_TOLERANCE) return index;
  }

  return lastIndex;
}

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} [carousel] - This section's `<slideshow-component>` elements.
 */

/**
 * Makes this section's carousels advance ONE card per arrow click.
 *
 * THE PROBLEM: `slideshow-component` pages by the viewport. `assets/slideshow.js` computes
 *
 *   get nextIndex() {
 *     const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;
 *     return current + modifier;
 *   }
 *
 * so with four cards on screen every arrow click jumped four cards, skipping the three in
 * between.
 *
 * WHY IT IS NOT FIXED IN slideshow.js: that getter is on the shared Slideshow class, which every
 * carousel in the theme instantiates. Changing it there would re-pace all of them.
 *
 * WHAT THIS DOES INSTEAD: redefines the two index getters on THIS section's slideshow instances
 * only, leaving the class untouched. That single pair is enough, because everything downstream
 * reads through it:
 *
 *   - `next()` / `previous()` call `select(this.nextIndex)` / `select(this.previousIndex)`
 *   - the arrow-disabling line in `set current()` reads
 *     `next.disabled = !infinite && this.nextIndex >= slides.length`
 *
 * so stepping and the disabled state stay consistent with each other for free.
 *
 * FAILURE MODE: if a future Horizon release renames those getters, `#stepByOne` finds nothing to
 * override and bails. The carousel then goes back to paging by viewport — a visible regression,
 * not a broken page.
 *
 * @extends {Component<Refs>}
 */
export class SlCollectionCardsComponent extends Component {
  /**
   * Slideshows already patched. A WeakSet rather than a flag on the element so nothing is written
   * onto a component this section does not own, and so entries are dropped with the elements.
   * @type {WeakSet<object>}
   */
  #stepped = new WeakSet();

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    // Patch now, measure later: the ResizeObserver below fires its first callback right after the
    // next layout and runs #refreshControls with real sizes. Measuring here as well forced a
    // synchronous layout during module evaluation — and, as noted below, usually read 0 anyway.
    this.#applyToAll({ refresh: false });

    // `slideshow:select` bubbles, so listening on the section catches every carousel inside it.
    // It fires whenever the current index changes — by arrow, by swipe or by tapping a segment —
    // which is exactly when the reachable-page count is worth re-checking.
    this.addEventListener(SlideshowSelectEvent.eventName, this.#refreshControls, {
      signal: this.#controller.signal,
    });

    // How many cards fit is a function of the container width, so both the reachable-page count
    // and the arrow's disabled state change on resize. A ResizeObserver also fires once right
    // after it starts observing, which is what gives the first correct pass — at connect time the
    // track has usually not been laid out yet and every measurement is still 0.
    this.#resizeObserver = new ResizeObserver(() => this.#refreshControls());
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /**
   * The section can be re-rendered by the theme editor and by the Section Rendering API, which
   * replaces the slideshow elements with fresh ones. Re-applying here re-patches the new
   * instances; the WeakSet keeps the surviving ones from being done twice.
   */
  updatedCallback() {
    super.updatedCallback?.();
    this.#applyToAll();
  }

  /** @param {{ refresh?: boolean }} [options] */
  #applyToAll({ refresh = true } = {}) {
    // `ref="carousel[]"` yields an array, but only when at least one slideshow rendered — a
    // section showing four or fewer cards as a static grid has none.
    for (const carousel of this.refs.carousel ?? []) this.#stepByOne(carousel);
    if (refresh) this.#refreshControls();
  }

  /**
   * Re-derives both controls for every carousel in the section.
   *
   * The arrow refresh is not decoration: `next.disabled` is only ever written by the theme's
   * `set current()` accessor, so without re-assigning `current` the arrow keeps whatever state it
   * was given at the previous viewport. Resizing across a width that changes how many cards fit
   * would otherwise leave it stuck — enabled at the end of the track, or dead before it.
   */
  #refreshControls = () => {
    for (const carousel of this.refs.carousel ?? []) {
      this.#syncDots(carousel);
      // Re-runs `set current()`, which recomputes `next.disabled` through the patched getter.
      // `set current()` writes DOM only — it dispatches no event — so this cannot recurse.
      if ('current' in carousel) carousel.current = carousel.current;
    }
  };

  /**
   * Trims the mobile progress bar to one segment PER REACHABLE POSITION instead of one per slide.
   *
   * `slideshow-controls` renders a segment for every slide — twelve cards, twelve segments — and
   * `set current()` fills whichever segment matches the current index. But `current` is the index
   * of the LEADING visible slide, so on a track that shows two cards at a time it stops at 10 of
   * 11: scrolled hard to the end, slides 10 and 11 are both on screen and 10 is the leading one.
   * The final segment was therefore unreachable and never filled, which is what the bar was
   * reported for.
   *
   * The number of distinct scroll positions is `slides - visible + 1`, so that is how many
   * segments the bar should have. The extras are hidden rather than removed: they stay in
   * `refs.dots`, so the slideshow's own index-to-segment loop keeps lining up, and they come back
   * by themselves when a wider viewport shows fewer cards at once.
   *
   * @param {HTMLElement} carousel - A `<slideshow-component>` element.
   */
  #syncDots(carousel) {
    const { dots } = /** @type {{ dots?: HTMLElement[] }} */ (carousel.refs ?? {});
    if (!Array.isArray(dots) || dots.length === 0) return;

    // The SAME ceiling the arrows use, so the bar has a segment for exactly the positions they
    // can reach — a bar that ran longer than the arrows was the other half of this bug.
    const reachable = lastReachableIndex(carousel);

    // Unmeasurable — leave the bar whole rather than guessing and hiding real segments. A later
    // resize or select re-runs this with real measurements.
    if (reachable == null) return;

    const pages = reachable + 1;

    for (const [index, dot] of dots.entries()) {
      const segment = dot.parentElement;
      if (!segment) continue;

      segment.hidden = index >= pages;

      // `slideshow-controls` rounds the right end of the bar with `li:last-child`, and that still
      // matches the hidden twelfth segment — so without this the visible end would render square.
      segment.classList.toggle('sl-collection-cards__segment--last', index === pages - 1);
    }
  }

  /**
   * @param {HTMLElement} carousel - A `<slideshow-component>` element.
   */
  #stepByOne(carousel) {
    if (this.#stepped.has(carousel)) return;

    // Only touch an element that really is the slideshow this override was written against.
    // `nextIndex` and `previousIndex` are prototype getters, so `in` sees them; `current` is the
    // value both replacements are built on.
    if (!('current' in carousel) || !('nextIndex' in carousel) || !('previousIndex' in carousel)) return;

    Object.defineProperty(carousel, 'nextIndex', {
      configurable: true,
      get() {
        // Measured from scroll geometry rather than from `visibleSlides.length` — see
        // `lastReachableIndex` for the viewport-dependent card the subtraction used to lose.
        const lastReachable = lastReachableIndex(this);

        // Track not measurable on this read. Never return the disabling sentinel here: the
        // component upgrades before the track is laid out, `set current()` would latch
        // `next.disabled = true`, and with no resize or select to follow the arrows would sit
        // dead on a freshly loaded page. Enabled is the safe default — index 0 always has a
        // next — and the first measured pass corrects it.
        if (lastReachable == null) return this.current + 1;

        // Below the ceiling, step one card — the whole point of this override.
        if (this.current < lastReachable) return this.current + 1;

        // At (or past) it, hand back an out-of-range index on purpose. `set current()` disables
        // the next arrow with `nextIndex >= slides.length`, so this is what makes the arrow go
        // dead exactly when the last card is fully in view — not a card earlier, and not three
        // clicks later.
        //
        // Without a ceiling the arrow stayed lit for three more clicks on a 12-card row:
        // `current` climbed 8 -> 11 while `scrollLeft` sat pinned at its maximum, so the button
        // responded but the carousel did not visibly move.
        //
        // Nothing ever calls `select()` with this value: by the time it is returned the button
        // that would have used it is already disabled.
        return this.slides.length;
      },
    });

    Object.defineProperty(carousel, 'previousIndex', {
      configurable: true,
      get() {
        return this.current - 1;
      },
    });

    this.#stepped.add(carousel);

    // The arrows were disabled using the OLD paging maths when the slideshow first set its
    // current index, which happens before this runs. Re-assigning the same value re-runs the
    // `set current()` accessor, which recomputes `next.disabled` through the new getter.
    carousel.current = carousel.current;
  }
}

if (!customElements.get('sl-collection-cards-component')) {
  customElements.define('sl-collection-cards-component', SlCollectionCardsComponent);
}
